import { Edit2, Filter, Plus, Search, Trash2, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import { buildApiUrl, formatDate } from "../../lib/utils";
import {
  Subscription,
  SubscriptionSearchRequest,
  SubscriptionSearchResponse,
} from "./@types";
import CreateSubscriptionModal from "./components/CreateSubscriptionModal";
import ExpiryStatus from "./components/ExpiryStatus";
import SubscriptionStats from "./components/SubscriptionStats";
import { useGetSubscriptionsMutation } from "./subscriptionApiSlice";

const SubscriptionDashboard: React.FC = () => {
  const navigate = useNavigate();
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [noOfVehicles, setNoOfVehicles] = useState(0);
  const [dateAddedFilter, setDateAddedFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedSubscription, setSelectedSubscription] =
    useState<Subscription | null>(null);
  const [openModal, setOpenModal] = useState("");

  // RTK Query mutation
  const [getSubscription, { data: subscriptionData, isLoading }] =
    useGetSubscriptionsMutation();

  const fetchSubscription = useCallback(async () => {
    const requestBody: SubscriptionSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      keyword: searchTerm || undefined,
      dateAdded: dateAddedFilter || undefined,
    };

    try {
      await getSubscription(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch subscriptions:", err);
    }
  }, [currentPage, pageSize, searchTerm, noOfVehicles, getSubscription]);

  // Fetch subscriptions on component mount and when filters change
  useEffect(() => {
    fetchSubscription();
  }, [
    currentPage,
    pageSize,
    searchTerm,
    noOfVehicles,
    dateAddedFilter,
    fetchSubscription,
  ]);

  // Table columns configuration
  const columns: Column<Subscription>[] = [
    {
      key: "userName",
      header: "Subscribed users",
      width: "",
      render: (value: string, item: Subscription) => (
        <div className="flex items-center space-x-3">
          {item.companyImageUrl ? (
            <img
              src={buildApiUrl(item.companyImageUrl)}
              alt={`${item.companyName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.customer.name}</span>
            <span className="text-pColor capitalize">
              {item.customer.email}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "planTier",
      header: "Plan",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "maxVehicles",
      header: "No of vehicles",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "startDate",
      header: "Subscription start",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },
    {
      key: "autoRenewal",
      header: "Auto renewal",
      width: "20",
      render: (value: boolean, item: Subscription) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Inactive",
              color: "text-gray-800 border border-gray-800",
            };
          if (item.isActive)
            return {
              label: "Active",
              color: "text-green-600 border border-green-600",
            };
          return {
            label: "Inactive",
            color: "text-gray-800 border border-gray-800",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`w-[60px] inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },

    {
      key: "endDate",
      header: "Status",
      width: "16",
      render: (value: string) => <ExpiryStatus endDate={value} />,
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Subscription) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "Edit",
                onClick: () => {
                  setSelectedSubscription(item);
                  setOpenModal("edit");
                },
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "Delete",
                onClick: () => {
                  setSelectedSubscription(item);
                  setOpenModal("delete");
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleModal = () => {
    setOpenModal("");
    fetchSubscription();
  };

  const response = subscriptionData?.data as
    | SubscriptionSearchResponse
    | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Dashboard Cards */}
        <div className="">
          <SubscriptionStats />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">
              All subscription
            </h2>
          </div>

          <div className="flex gap-3">
            <AppButton
              onClick={() => setOpenModal("add")}
              className="flex items-center px-2 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors min-w-[156px] h-[38px]"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add subscription
            </AppButton>
          </div>
        </div>

        {/* All subscriptions section */}
        <div className="space-y-6 bg-white rounded-[10px] p-4">
          {/* Filters and Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-[16px] pl-9 pr-4 py-2 border border-[#F6F6F6] bg-[#F6F6F6] rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-full sm:w-[367px] h-[42px]"
                />
              </div>

              {/* Filters */}
              <button
                // onClick={() => setOpenModal(true)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition"
              >
                <Filter className="h-4 w-4 text-gray-600" />
                <span className="text-sm text-gray-700 font-medium">
                  Filter
                </span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              emptyMessage="No subscriptions found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>

      <CreateSubscriptionModal
        isOpen={openModal === "add"}
        onClose={handleModal}
      />
    </div>
  );
};

export default SubscriptionDashboard;
